import api from 'services/api';
import auth from 'services/auth';
import session from './session';
import user from './user';
import { setTimezone } from 'utils';

export const PARTNER_FETCH = 'partner/fetch';
export const PARTNER_CREATE = 'client/create';
export const PARTNER_UPDATE = 'client/update';
export const PARTNER_VALIDATE = 'partner/validate';
export const PARTNER_LEAD_FETCH = 'partner/createFetch';
export const PARTNER_LEAD_CREATE = 'partner/createLead';
export const PARTNER_LEAD_UPDATE = 'partner/createUpdate';
export const PARTNER_FETCH_INVITED = 'partner/fetchInvited';
export const PARTNER_UPDATE_INVITED = 'partner/updateInvited';
export const PARTNER_CREATE_STORE = 'partner/createStore';
export const PARTNER_STORE_INVITE_FETCH = 'partner/storeInvite/fetch';
export const PARTNER_STORE_INVITE_PARTNERS = 'partner/storeInvite/partners';
export const PARTNER_STORE_INVITE_ACCEPT = 'partner/storeInvite/accept';

// Store invite errors are shown on the accept page itself
const toError = (err) => ({ error: { message: err.message } });

const actions = {
  fetch(query) {
    return {
      type: PARTNER_FETCH,
      payload: api.get('/partner', query).then((partner) => {
        if (partner) {
          setTimezone(partner.timezone);
        }

        return partner;
      }),
    };
  },

  create(id) {
    return (dispatch) => {
      return dispatch({
        type: PARTNER_CREATE,
        payload: api.post(`/partner/${id}`),
      }).then((result) => {
        if (!result?.session_id) {
          return result;
        }

        // The create response is the partner record itself; its own id is the partner id
        auth.setLoggedInPartnerSessionId(result.session_id, result.id);

        return Promise.all([
          dispatch(session.fetch()),
          dispatch(this.fetch()),
          dispatch(user.fetch()),
        ]).then(() => result);
      });
    };
  },

  update(data) {
    return {
      type: PARTNER_UPDATE,
      payload: api.put('/partner', data).then((partner) => {
        if (partner) {
          setTimezone(partner.timezone);
        }

        return partner;
      }),
    };
  },

  transferOwnership(userId) {
    return {
      type: PARTNER_UPDATE,
      payload: api.post('/partner/transfer-ownership', { user_id: userId }),
    };
  },

  validate(data) {
    return {
      type: PARTNER_VALIDATE,
      payload: api.post('/partner/validate', data),
      showLoading: false,
    };
  },

  fetchLead(id) {
    return {
      type: PARTNER_LEAD_FETCH,
      payload: api.get(`/partner/leads/${id}`),
    };
  },

  createLead(data) {
    return {
      type: PARTNER_LEAD_CREATE,
      payload: api.post('/partner/leads', data),
    };
  },

  completeLead(id, data) {
    return {
      type: PARTNER_LEAD_UPDATE,
      payload: api.post(`/partner/leads/${id}/complete`, data),
    };
  },

  // Mails the lead's verification code; the same call resends it
  sendLeadCode(id) {
    return {
      type: PARTNER_LEAD_UPDATE,
      payload: api.put(`/partner/leads/${id}`),
    };
  },

  fetchInvited(id) {
    return {
      type: PARTNER_FETCH_INVITED,
      payload: api.get(`/partner/signup/${id}`),
    };
  },

  updateInvited(id, data) {
    return {
      type: PARTNER_UPDATE_INVITED,
      payload: api.post(`/partner/signup/${id}`, data),
    };
  },

  // A store's invite to connect it to one of the user's partner dashboards
  fetchStoreInvite(code) {
    return {
      type: PARTNER_STORE_INVITE_FETCH,
      payload: api.get(`/partner/store-invites/${code}`).catch(toError),
    };
  },

  // The logged in user's partner dashboards to connect the store to
  fetchStoreInvitePartners(code) {
    return {
      type: PARTNER_STORE_INVITE_PARTNERS,
      payload: api
        .get(`/partner/store-invites/${code}/partners`)
        .catch(toError),
    };
  },

  // Connects the store to the session's partner dashboard
  acceptStoreInvite(code) {
    return {
      type: PARTNER_STORE_INVITE_ACCEPT,
      payload: api.post(`/partner/store-invites/${code}/accept`).catch(toError),
    };
  },

  createStore(data) {
    return (dispatch, getState) => {
      const state = getState();
      const partnerId = state.partner?.id || state.session?.partner_id;

      return dispatch({
        type: PARTNER_CREATE_STORE,
        payload: api.post('/client', {
          ...data,
          partner_id: partnerId,
        }),
      });
    };
  },
};

export const initialState = {
  lead: null,
  invited: null,
};

export function reducer(state = initialState, action) {
  if (action.payload && action.payload.error) {
    return state;
  }

  switch (action.type) {
    case PARTNER_FETCH:
    case PARTNER_CREATE:
    case PARTNER_UPDATE:
      return {
        ...state,
        ...action.payload,
      };
    case PARTNER_LEAD_FETCH:
    case PARTNER_LEAD_CREATE:
      return {
        ...state,
        lead: action.payload,
      };
    case PARTNER_FETCH_INVITED:
      return {
        ...state,
        invited: action.payload,
      };
    default:
      return state;
  }
}

export default actions;
